/**
 * GLB Viewer Component
 * Three.js based 3D model viewer for product pages.
 * Plays embedded GLB animations once on load; exposes a replay button after they finish.
 */

import * as THREE from 'https://esm.sh/three@0.160.0';
import { GLTFLoader } from 'https://esm.sh/three@0.160.0/examples/jsm/loaders/GLTFLoader';
import { OrbitControls } from 'https://esm.sh/three@0.160.0/examples/jsm/controls/OrbitControls';

class GLBViewer extends HTMLElement {
  constructor() {
    super();
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.controls = null;
    this.model = null;
    this.animationId = null;
    this.isLoading = true;
    this.clock = new THREE.Clock();
    this.mixer = null;
    this.animationAction = null;
    this.elapsedTime = 0;
    this.idleBasePosition = null;
    this.hasUserInteracted = false;
    this.shadowPlane = null;
  }

  connectedCallback() {
    this.modelUrl = this.getAttribute('data-model-url');
    this.posterUrl = this.getAttribute('data-poster-url');
    this.bgColor = this.getAttribute('data-bg-color') || '#f5f5f5';

    if (!this.modelUrl) {
      console.error('GLB Viewer: No model URL provided');
      return;
    }

    this.render();
    this.initScene();
    this.loadModel();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  render() {
    this.innerHTML = `
      <div class="glb-viewer">
        <div class="glb-viewer__loading" data-loading>
          <div class="glb-viewer__spinner"></div>
          <p>Cargando modelo 3D...</p>
        </div>
        <div class="glb-viewer__error" data-error style="display: none;">
          <p>Error al cargar el modelo 3D</p>
        </div>
        <canvas data-canvas></canvas>
        <div class="glb-viewer__controls">
          <button type="button" class="glb-viewer__button glb-viewer__button--replay" data-replay title="Reproducir animación" aria-label="Reproducir animación">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <polygon points="6,4 20,12 6,20"/>
            </svg>
          </button>
          <button type="button" class="glb-viewer__button" data-reset title="Resetear vista" aria-label="Resetear vista">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
              <path d="M21 3v5h-5"/>
              <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
              <path d="M3 21v-5h5"/>
            </svg>
          </button>
        </div>
      </div>
    `;

    this.canvas = this.querySelector('[data-canvas]');
    this.loadingEl = this.querySelector('[data-loading]');
    this.errorEl = this.querySelector('[data-error]');
    this.resetBtn = this.querySelector('[data-reset]');
    this.replayBtn = this.querySelector('[data-replay]');

    this.resetBtn.addEventListener('click', () => this.resetCamera());
    this.replayBtn.addEventListener('click', () => this.replayAnimation());
  }

  initScene() {
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(this.bgColor);

    const aspect = this.canvas.clientWidth / (this.canvas.clientHeight || 1);
    this.camera = new THREE.PerspectiveCamera(45, aspect, 0.1, 1000);
    this.camera.position.set(0, 1, 3);

    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      alpha: true,
    });
    this.renderer.setSize(this.canvas.clientWidth, this.canvas.clientHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    // Sombras activadas solo para el plano
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.VSMShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.2;

    // Luz ambiental más intensa
    const ambientLight = new THREE.AmbientLight(0xffffff, 1.2);
    this.scene.add(ambientLight);

    // Luz direccional cenital SOLO para la sombra
    const shadowLight = new THREE.DirectionalLight(0xffffff, 0);
    shadowLight.position.set(0, 10, 0);
    shadowLight.castShadow = true;
    shadowLight.shadow.mapSize.width = 2048;
    shadowLight.shadow.mapSize.height = 2048;
    shadowLight.shadow.camera.near = 0.5;
    shadowLight.shadow.camera.far = 50;
    shadowLight.shadow.camera.left = -5;
    shadowLight.shadow.camera.right = 5;
    shadowLight.shadow.camera.top = 5;
    shadowLight.shadow.camera.bottom = -5;
    shadowLight.shadow.bias = -0.0001;
    shadowLight.shadow.radius = 8;
    this.scene.add(shadowLight);

    // Luces para iluminar el modelo (sin sombras)
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1.0);
    directionalLight.position.set(5, 5, 5);
    directionalLight.castShadow = false;
    this.scene.add(directionalLight);

    const fillLight = new THREE.DirectionalLight(0xffffff, 0.6);
    fillLight.position.set(-5, 0, -5);
    this.scene.add(fillLight);

    const bottomLight = new THREE.DirectionalLight(0xffffff, 0.5);
    bottomLight.position.set(0, -5, 0);
    this.scene.add(bottomLight);

    const backLight = new THREE.DirectionalLight(0xffffff, 0.4);
    backLight.position.set(0, 3, -5);
    this.scene.add(backLight);

    // Plano invisible que recibe la sombra
    const planeGeometry = new THREE.PlaneGeometry(10, 10);
    const planeMaterial = new THREE.ShadowMaterial({
      opacity: 0.3,
    });
    this.shadowPlane = new THREE.Mesh(planeGeometry, planeMaterial);
    this.shadowPlane.rotation.x = -Math.PI / 2;
    this.shadowPlane.receiveShadow = true;
    this.scene.add(this.shadowPlane);

    this.controls = new OrbitControls(this.camera, this.canvas);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;
    this.controls.enableZoom = false;
    this.controls.enablePan = false;
    
    // Bloquear rotación vertical - solo horizontal
    this.controls.minPolarAngle = Math.PI / 2; // 90 grados
    this.controls.maxPolarAngle = Math.PI / 2; // 90 grados (misma altura siempre)

    this.initialCameraPosition = this.camera.position.clone();
    this.initialControlsTarget = this.controls.target.clone();

    this.controls.addEventListener('start', () => { this.hasUserInteracted = true; });

    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(this.canvas);

    this.animate();
  }

  async loadModel() {
    const loader = new GLTFLoader();

    try {
      const gltf = await loader.loadAsync(this.modelUrl);

      this.model = gltf.scene;

      // Center and scale model to fit view
      const box = new THREE.Box3().setFromObject(this.model);
      const size = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z);
      const scale = 2 / maxDim;
      this.model.scale.multiplyScalar(scale);

      const scaledBox = new THREE.Box3().setFromObject(this.model);
      const scaledCenter = scaledBox.getCenter(new THREE.Vector3());
      this.model.position.sub(scaledCenter);

      // El modelo proyecta sombra pero NO tiene sombras sobre él
      this.model.traverse((node) => {
        if (node.isMesh) {
          node.castShadow = true;
          node.receiveShadow = false;
        }
      });

      this.scene.add(this.model);

      // Posicionar el plano de sombra justo debajo del modelo
      const modelBottom = scaledBox.min.y;
      this.shadowPlane.position.y = modelBottom - 0.2;

      // Position camera based on model size
      const isMobile = window.innerWidth <= 749;
      const distanceFactor = isMobile ? 1.2 : 0.9;
      const distance = size.length() * scale * distanceFactor;
      
      this.camera.position.set(0, distance * 0, distance);
      this.camera.lookAt(0, 0, 0);
      this.controls.target.set(0, 0, 0);
      this.controls.update();

      this.initialCameraPosition = this.camera.position.clone();
      this.initialControlsTarget = this.controls.target.clone();
      this.idleBasePosition = this.camera.position.clone();

      // Set up animations
      if (gltf.animations && gltf.animations.length > 0) {
        this.mixer = new THREE.AnimationMixer(this.model);
        this.animationAction = this.mixer.clipAction(gltf.animations[0]);
        this.animationAction.setLoop(THREE.LoopOnce);
        this.animationAction.clampWhenFinished = true;
        this.animationAction.play();

        this.mixer.addEventListener('finished', () => this.onAnimationFinished());

        this.clock.getDelta();
      }

      this.hideLoading();
      this.isLoading = false;
    } catch (error) {
      console.error('GLB Viewer: Error loading model', error);
      this.showError();
    }
  }

  animate() {
    this.animationId = requestAnimationFrame(() => this.animate());

    const delta = this.clock.getDelta();
    this.elapsedTime += delta;

    if (this.mixer) {
      this.mixer.update(delta);
    }

    if (this.controls) {
      this.controls.update();
    }

    if (this.renderer && this.scene && this.camera) {
      // Balanceo sutil del modelo en el eje Y (arriba-abajo)
      if (this.model) {
        const t = this.elapsedTime;
        // Balanceo muy sutil en Y
        this.model.rotation.x = Math.sin(t * 0.8) * 0.08;
        this.model.position.y = Math.sin(t * 0.5) * 0.07; // Ajusta 0.05 para más o menos balanceo
      }

      if (!this.hasUserInteracted && this.idleBasePosition) {
        const restorePos = this.camera.position.clone();
        const restoreQuat = this.camera.quaternion.clone();

        const t = this.elapsedTime;
        // Solo rotación horizontal (X), sin movimiento vertical
        this.camera.position.x = this.idleBasePosition.x + Math.sin(t * 0.8) * 0.2;
        this.camera.lookAt(this.controls.target);

        this.renderer.render(this.scene, this.camera);

        this.camera.position.copy(restorePos);
        this.camera.quaternion.copy(restoreQuat);
      } else {
        this.renderer.render(this.scene, this.camera);
      }
    }
  }

  replayAnimation() {
    if (!this.animationAction) return;
    this.animationAction.reset();
    this.animationAction.play();
  }

  onAnimationFinished() {}

  handleResize() {
    if (!this.camera || !this.renderer) return;

    const width = this.canvas.clientWidth;
    const height = this.canvas.clientHeight;

    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
  }

  resetCamera() {
    if (!this.camera || !this.controls) return;

    this.camera.position.copy(this.initialCameraPosition);
    this.controls.target.copy(this.initialControlsTarget);
    this.controls.update();
  }

  hideLoading() {
    if (this.loadingEl) {
      this.loadingEl.style.display = 'none';
    }
  }

  showError() {
    if (this.loadingEl) {
      this.loadingEl.style.display = 'none';
    }
    if (this.errorEl) {
      this.errorEl.style.display = 'flex';
    }
  }

  cleanup() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.mixer) {
      this.mixer.stopAllAction();
    }
    if (this.controls) {
      this.controls.dispose();
    }
    if (this.renderer) {
      this.renderer.dispose();
    }
    if (this.shadowPlane) {
      if (this.shadowPlane.geometry) this.shadowPlane.geometry.dispose();
      if (this.shadowPlane.material) this.shadowPlane.material.dispose();
    }
    if (this.scene) {
      this.scene.traverse((object) => {
        if (object.geometry) object.geometry.dispose();
        if (object.material) {
          if (Array.isArray(object.material)) {
            object.material.forEach((m) => m.dispose());
          } else {
            object.material.dispose();
          }
        }
      });
    }
  }
}

customElements.define('glb-viewer', GLBViewer);